/**
 * Helper: Log error
 * ------------------------------------------------------------------------------
 * - Helper function for logging errors.
 */
import {
  captureMessage,
  captureException,
  setContext,
} from '@sentry/browser'

/**
 * Valid Sentry Levels
 * https://docs.sentry.io/platforms/javascript/usage/set-level/
 */
const validLevels = [
  'debug',
  'error',
  'fatal',
  'info',
  'log',
  'warning',
]

/**
 * Global logging function to output errors and send to Sentry.
 * @example
 *   mint.Log('error', 'Standard error', <ErrorObject>)
 *   mint.Log('warning', 'This is my message without an error')
 *   mint.Log('fatal', 'Message', error, {
 *     key: 'Additional Data'
 *   })
 *
 * @param {String} level - Valid Sentry log level.
 * @param {String} [message] - Additional message context.
 * @param {*} [error] - Error/Exception. Ignore if just sending message.
 * @param {Object} [additionalData] - Additional data to send to Sentry.
 * @see {@link: https://mint-velvet.atlassian.net/wiki/spaces/TECHNOLOGY/pages/257130497/Logging+Errors+and+Sentry}
 */
export function log(
  level = 'error',
  message = false,
  error = false,
  additionalData = false,
) {

  /**
   * Sentry not enabled.
   * Likely a development or unpublished theme - leverage defaults.
   */
  if (!mint?.flags?.sentry?.enable) {
    switch (level) {
      case 'error':
      case 'fatal':
        if (error instanceof Error) {
          throw error
        }

        throw new Error(message)

      case 'warning':
        console.warn(message, error, additionalData)
        break

      default:
        console.log(message, error, additionalData)
        break
    }

    return
  }

  /**
   * Sentry is enabled - ensure logs are directed there.
   */
  let consoleLevel = getLocalConsoleLevel(level)

  if (!validLevels.includes(level)) {
    console.warn(`[Mint] ${level} is an invalid Sentry level. Error may not be reported to Sentry.` )
  }

  /**
   * An error has been passed in.
   * - captureException will send to Sentry and output to the browser.
   * https://docs.sentry.io/platforms/javascript/enriching-events/context/
   */
  const exceptionLevels = ['error', 'fatal']

  if (exceptionLevels.includes(level)) {
    captureException(error, {
      level,
      extra: {
        message,
        additionalData,
      },
    })

    captureException(error, level)

    if (error instanceof Error) {
      throw error
    }

    throw new Error(message)
  }

  /**
   * Otherwise,
   */
  captureMessage(message, {
    level,
    extra: {
      message,
    },
  })

  consoleLevel = (level === 'warning' ? 'warn' : level)

  console[consoleLevel](message, additionalData)
}

/**
 * Converts a Sentry error level to a Console API compatible level.
 * - Used for outputting correct level logs when Sentry is disabled.
 * @param {String} userLevel - The 'Log Error' level passed from a developer.
 * @returns {String}
 */
function getLocalConsoleLevel(userLevel) {
  if (!validLevels.includes(userLevel)) {
    return 'log'
  }

  if (userLevel === 'fatal') {
    return 'error'
  }

  if (userLevel === 'warning') {
    return 'warn'
  }

  return userLevel
}
